import { Box, useTheme } from "@mui/material";
import { tokens } from "../../../../theme";
import { useState, useEffect } from "react";
import All from "./views/All";
import ConceptInvoiceProducts from "./views/ConceptInvoiceProducts";
import OrdersWithoutSendingCosts from "./views/OrdersWithoutSendingCosts";
import ReturnsWithoutSendingCosts from "./views/ReturnsWithoutSendingCosts";
import ShipmentsWithoutSendingCosts from "./views/ShipmentsWithoutSendingCosts";
import Mutations from "./views/Mutations";
import ShippingCostOverview from "./views/ShippingCostOverview";
import { useTranslation } from 'react-i18next';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import EuroIcon from '@mui/icons-material/Euro';
import SavingsIcon from '@mui/icons-material/Savings';
import BalanceIcon from '@mui/icons-material/Balance';
import TabView3 from "../../../../components/theme/TabView3/TabView3";
import StatCard from "../../../../components/theme/cards/StatCard";
import StatCardRow from "../../../../components/theme/cards/StatCardRow";
import InvoiceTotalsPanel from "../../../../components/global/dashboard/InvoiceTotalsPanel";
import Text from "../../../../components/theme/text/Text";
import { warehouseFinancialInvoicesConfig } from "./warehouseFinancialInvoicesConfig";
import { useGetConceptInvoiceInvoiceProductsCountQuery, useGetShippingCostOverviewTotalsQuery } from "../../../../newapi/financial/invoiceSlice";
import { useTopbar } from "../../../../contexts/TopbarProvider";
import { formatNumber, formatPrice } from "../../../../utils/helpers";

// The shipping cost overview totals sit above the tab view, only while that tab is open. The tab
// reports its active filter up through onFilterChange, which drives this card row, so the
// totals always describe the same period and carrier as the rows below them.
const ShippingCostTotals = ({ filter }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { data: totalsData, isLoading, isFetching } = useGetShippingCostOverviewTotalsQuery(filter);
    const totals = totalsData?.totals;
    const totalsLoading = isLoading || isFetching;

    // A negative saldo means the carrier charged more than the customer was invoiced.
    const saldoColor = (saldo) => saldo < 0 ? colors.red[400] : colors.green[400];

    return (
        <Box sx={{ px: 3, pb: 1 }}>
            <StatCardRow sx={{ pb: 1 }}>
                <StatCard
                    label={`${t("shipments")} / ${t("orders")}`}
                    value={[formatNumber(i18n, totals?.totalShipments ?? 0), formatNumber(i18n, totals?.totalOrders ?? 0)]}
                    icon={<LocalShippingIcon className="logo"/>}
                    isLoading={totalsLoading}
                />
                <StatCard
                    label={t("total_invoiced")}
                    value={formatPrice(i18n, totals?.totalInvoiced ?? 0)}
                    icon={<ReceiptLongIcon className="logo"/>}
                    isLoading={totalsLoading}
                />
                <StatCard
                    label={t("total_paid_to_carrier")}
                    value={formatPrice(i18n, totals?.totalPaid ?? 0)}
                    icon={<PaymentsIcon className="logo"/>}
                    isLoading={totalsLoading}
                />
                <StatCard
                    label={`${t("shipping_costs")} / ${t("surcharges")}`}
                    value={[formatPrice(i18n, totals?.totalShippingCosts ?? 0), formatPrice(i18n, totals?.totalAdjustmentCharges ?? 0)]}
                    icon={<EuroIcon className="logo"/>}
                    isLoading={totalsLoading}
                />
                <StatCard
                    label={t("total_saldo")}
                    value={formatPrice(i18n, totals?.totalSaldo ?? 0)}
                    icon={<BalanceIcon className="logo"/>}
                    valueColor={saldoColor(totals?.totalSaldo ?? 0)}
                    isLoading={totalsLoading}
                />
                <StatCard
                    label={t("average_saldo_per_order")}
                    value={formatPrice(i18n, totals?.averageSaldoPerOrder ?? 0)}
                    icon={<SavingsIcon className="logo"/>}
                    valueColor={saldoColor(totals?.averageSaldoPerOrder ?? 0)}
                    isLoading={totalsLoading}
                />
            </StatCardRow>

            {/* Revenue is filled in when the customer invoice exists, so a period that is
                still being invoiced would otherwise read as pure loss. */}
            {!totalsLoading && totals?.ordersWithoutRevenue > 0 && (
                <Text light>
                    {t("orders_without_invoiced_costs_yet", { count: totals.ordersWithoutRevenue })}
                </Text>
            )}
        </Box>
    );
};

const FinancialInvoices = () => {
    const { t, ready, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { data : conceptProductCount } = useGetConceptInvoiceInvoiceProductsCountQuery();
    const [config, setConfig] = useState(warehouseFinancialInvoicesConfig());

    // Set while the shipping cost overview tab is open; null clears the cards for every other tab.
    const [shippingTotalsFilter, setShippingTotalsFilter] = useState(null);

    // Same for the invoice totals panel, which belongs to the All tab.
    const [allTabActive, setAllTabActive] = useState(false);

    const {showExtraTitle,clearExtraTitle } = useTopbar();

    useEffect(() => {
        setConfig(warehouseFinancialInvoicesConfig(conceptProductCount));
    }, [conceptProductCount]);
  
  
    useEffect(() => {
        showExtraTitle(t("invoices"))

        return () => {
            clearExtraTitle();
        };
    }, [showExtraTitle,clearExtraTitle,t,i18n.language])
      
    return (
        <Box sx={{background:colors.bg["tertiary"], width:1,paddingTop:9}}>
            {ready ?
            <>
                {allTabActive && <InvoiceTotalsPanel />}
                {shippingTotalsFilter && <ShippingCostTotals filter={shippingTotalsFilter} />}

                <TabView3
                    tabConfig={config}
                    // ref={ref}
                    sx={{
                        minHeight:"calc(100% - 190px)",
                        background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        position:"relative",
                        zIndex:2,
                        display:"flex",
                        flexDirection:"column",

                        "&:before" : {
                            content: "''",
                            display:"block",
                            width:"32px",
                            height:"32px",
                            position:"absolute",
                            left:0,
                            top:0,
                            zIndex:1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                            top:"40px",
                            height:"48px",
                            width:"24px",
                            borderTopLeftRadius:"24px",
                            boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                            transition:"250ms"
                        },

                        "&:after" : {
                            content: "''",
                            display:"block",
                            width:"32px",
                            height:"32px",
                            position:"absolute",
                            top:0,
                            zIndex:1,
                            backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                            top:"40px",
                            right:0,
                            height:"48px",
                            width:"24px",
                            borderTopLeftRadius:"24px",
                            boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                            transform:"scaleX(-1)",
                            transition:"250ms"
                        },

                        "@media screen and (max-width: 47.9375em)" : {
                            "&:after, &:before" : {
                                top:"-0px"
                            }
                        }
                    }}
                >
                    <>
                        <All config={config.all} onActiveChange={setAllTabActive}/>
                        <ConceptInvoiceProducts config={config.conceptInvoiceProducts}/>
                        <OrdersWithoutSendingCosts config={config.ordersWithoutCosts}/>
                        <ReturnsWithoutSendingCosts config={config.returnsWithoutCosts}/>
                        <ShipmentsWithoutSendingCosts config={config.shipmentsWithoutCosts}/>
                        <Mutations config={config.mutations}/>
                        <ShippingCostOverview config={config.shippingCostOverview} onFilterChange={setShippingTotalsFilter}/>
                        
                    </>
                </TabView3>
                {/* </TabView> */}
            </>
            :null}
        </Box>
    );
};

export default FinancialInvoices;